import { useId, useState } from 'react'
import type { JSX } from 'react'

import { Sidebar } from '@buffer-mono/popcorn'

import {
  DraggableSavedViewItem,
  DraggableSavedViewsProvider,
  useDraggableSavedViewsContext,
} from './DraggableSavedViewsSection'
import { useReorderSavedViewsMutation, useSavedViewsQuery } from './hooks'
import type { SavedView } from './hooks'
import { SavedViewItem, savedViewItemStyles } from './SavedViewItem'
import { SavedViewsSectionHeader } from './SavedViewsSectionHeader'
import { SavedViewsSectionSkeleton } from './SavedViewsSectionSkeleton'
import { SidebarErrorState } from '../SidebarErrorState'

import styles from './SavedViewsSection.module.css'

/** Sidebar section listing the organization's saved views. */
export function SavedViewsSection(): JSX.Element | null {
  const [isExpanded, setIsExpanded] = useState(true)
  const { savedViews, loading, error } = useSavedViewsQuery()
  const contentId = useId()

  if (!loading && !error && savedViews.length === 0) return null

  return (
    <Sidebar.Group className={styles.savedViewsSection}>
      <Sidebar.Separator className={styles.separator} />
      <Sidebar.GroupLabel className={styles.groupLabel}>
        <SavedViewsSectionHeader
          isExpanded={isExpanded}
          onToggle={(): void => setIsExpanded((prev) => !prev)}
          controlsId={contentId}
        />
      </Sidebar.GroupLabel>
      <Sidebar.GroupContent
        id={contentId}
        className={styles.groupContent}
        hidden={!isExpanded}
      >
        {error && (
          <SidebarErrorState
            heading="Failed to load saved views"
            error={error}
          />
        )}
        {!error && loading && <SavedViewsSectionSkeleton />}
        {!error && !loading && savedViews.length > 0 && (
          <SavedViewsList savedViews={savedViews} />
        )}
      </Sidebar.GroupContent>
    </Sidebar.Group>
  )
}

/**
 * Renders the list of saved views. With multiple views the list becomes
 * sortable via drag-and-drop, calling `useReorderSavedViewsMutation` with
 * the new id order on drop — the hook owns the optimistic cache update,
 * server refetch, and error toast. The reorder applies immediately on drop
 * with no confirmation step.
 */
interface SavedViewsListProps {
  savedViews: SavedView[]
}

function SavedViewsList({ savedViews }: SavedViewsListProps): JSX.Element {
  const [reorderSavedViews] = useReorderSavedViewsMutation()
  const isDraggable = savedViews.length > 1

  if (!isDraggable) {
    return (
      <Sidebar.List aria-label="Views">
        {savedViews.map((view) => (
          <Sidebar.ListItem key={view.id} className={savedViewItemStyles.row}>
            <SavedViewItem view={view} />
          </Sidebar.ListItem>
        ))}
      </Sidebar.List>
    )
  }

  return (
    <DraggableSavedViewsProvider
      views={savedViews}
      onReorder={(newViewIds): void => {
        void reorderSavedViews(newViewIds)
      }}
    >
      <DraggableSavedViewsList savedViews={savedViews} />
    </DraggableSavedViewsProvider>
  )
}

/**
 * Inner list component that consumes the provider's dragging state and
 * mirrors it onto `Sidebar.List` via `data-dragging`. The row CSS reads
 * that attribute to suppress hover effects mid-drag, so the wiring has
 * to happen inside the provider's subtree.
 */
function DraggableSavedViewsList({
  savedViews,
}: SavedViewsListProps): JSX.Element {
  const { isDragging } = useDraggableSavedViewsContext()

  return (
    <Sidebar.List aria-label="Views" data-dragging={isDragging || undefined}>
      {savedViews.map((view) => (
        <DraggableSavedViewItem key={view.id} view={view}>
          <SavedViewItem view={view} />
        </DraggableSavedViewItem>
      ))}
    </Sidebar.List>
  )
}
